import type { PostHogConfig } from 'posthog-js';
import { sanitizePostHogEvent } from './sanitize';

/**
 * PostHog Cloud のリージョン（管理画面リンク・Toolbar 用の ui_host）。
 *
 * GF-1889 で「EU リージョン送信」が確定済み（個情法 / 越境移転対応）。
 * プロジェクト概要上は GF-1890 での最終確定待ち表記が残っているため、
 * 変更が一箇所で済むよう定数として切り出す。
 * TODO(GF-1890): リージョン最終確定後にこの値が EU で正しいことを確認する。
 */
export const POSTHOG_UI_HOST = 'https://eu.posthog.com';

/**
 * PostHog Autocapture から要素（およびその子孫）を除外する CSS クラス名。
 *
 * ⚠️ posthog-js の除外判定は **この CSS クラス**のみを見る（`data-ph-no-capture` 属性は無視される）。
 * PII を含む入力・表示には、属性ではなくこのクラスを付与すること（GF-1936）。
 */
export const PH_NO_CAPTURE_CLASS = 'ph-no-capture';

/**
 * クライアント計測に必要な公開環境変数。
 * `NEXT_PUBLIC_*` はビルド時にクライアントバンドルへ焼き込まれる。
 */
export const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY;
export const POSTHOG_HOST = process.env.NEXT_PUBLIC_POSTHOG_HOST;

/**
 * KEY が無い環境（テスト / 未構築の dev / ローカル）では init を行わない。
 * これにより Vitest / Playwright 実行時に PostHog へイベントが飛ばない（GF-1897）。
 */
export const isPostHogConfigured = (): boolean => Boolean(POSTHOG_KEY);

/**
 * `posthog.init()` オプション。
 *
 * Autocapture 主軸の運用方針（選定理由書 / GF-1888）に従う。
 * クリック・フォーム submit・ページ遷移は自動取得し、手動イベントは最小限に絞る。
 */
export const POSTHOG_INIT_OPTIONS: Partial<PostHogConfig> = {
  // プロキシ経由（AdBlock 対策）。プロキシ方式は GF-1934 で確定した値を env で渡す。
  api_host: POSTHOG_HOST,
  ui_host: POSTHOG_UI_HOST,
  // ★ 選定の核心。事前イベント設計なしで全クリック・フォーム submit を自動取得する。
  autocapture: true,
  // App Router は SPA 遷移のためデフォルトだと初回しか取れない。history 変化で自動捕捉する。
  capture_pageview: 'history_change',
  // 匿名 profile を作らず MAU 課金を抑える（課金単位は Person profile 数）。
  person_profiles: 'identified_only',
  // Session Replay は Clarity と機能重複のため明示 OFF（コスト枠が別）。
  disable_session_recording: true,
  // 認証コード等が $current_url / $referrer 経由で送信されるのを防ぐ（セキュリティ F1 / GF-1889）。
  before_send: sanitizePostHogEvent,
  // URL・要素属性に紛れる一般的な PII（email 等）を自動マスクする（ph-no-capture の二重防御）。
  mask_personal_data_properties: true,
  // cookie は HTTPS 限定。本番は常に HTTPS、ローカル(http)は localStorage 永続化にフォールバックする。
  secure_cookie: process.env.NODE_ENV === 'production',
  loaded: (ph) => {
    // dev のみ window.posthog を露出（npm モジュール方式では既定で生えないため、コンソール確認用）。
    if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
      Object.assign(window, { posthog: ph });
    }
  },
};
